/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { ErrorBoundary } from '@umati/dashboard-common';
import FallbackDetail from 'components/detail/common/FallbackDetail';
import ScaleDetail from 'components/detail/scale/ScaleDetail';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MqttTopics from 'utilities/MqttTopics';
import SubscriptionComponent from 'utilities/SubscriptionComponent';
import SurfaceTechnologyDetail from 'views/SurfaceTechnologyDetail';
import TemplateTypeDetail from 'views/TemplateTypeDetail';
import Footer from './footer/footer';

interface ISubscribedDetailState {
  mqttData: any;
}

type ISubscribedDetailParams = {
  machine: string;
  fairTag?: string;
  typeId: string;
}

const SubscribedDetailByTypeDefiniton = {
  SurfaceTechnologyCoatingSystemType: SurfaceTechnologyDetail,
  SurfaceTechnologyObjectType: SurfaceTechnologyDetail,
  scale: ScaleDetail
};

export default function SubscribedDetail () {
  const params = useParams() as ISubscribedDetailParams;
  const typeId = params.typeId;
  const DetailComponent = SubscribedDetailByTypeDefiniton[typeId] || TemplateTypeDetail;
  const machineId = decodeURIComponent(params.machine);
  const fairTag = params.fairTag;
  const topic = MqttTopics.Machine(typeId, machineId);

  const [{ mqttData }, setState] = useState<ISubscribedDetailState>({
    mqttData: undefined
  });
  const [scrolledToTop, setScrolledToTop] = useState(false);

  const updateMachineViaMqtt = (data, topicPar) => {
    if (topicPar === topic) {
      setState({ mqttData: data });
    } else {
      console.log(`Topic not match: ${topic} ${topicPar}`);
    }
  };

  useEffect(
    () => {
      if (!scrolledToTop) {
        window.scrollTo(0, 0);
      }
      setScrolledToTop(true);
      setState({ mqttData: undefined });
    },
    [topic, scrolledToTop]
  );

  return (
    <main className="footer-wrapper" style={ { scrollBehavior: 'auto' }}>
      <SubscriptionComponent topic={topic} subscriptionCallback={updateMachineViaMqtt} />
      <ErrorBoundary<typeof DetailComponent['Props']> ErrorComponent={FallbackDetail} ErrorComponentProps={{ fairTag, machineId, typeId }}>
      <DetailComponent data={mqttData} fairTag={fairTag} machineId={machineId} typeId={typeId} />
      </ErrorBoundary>
      <div className="container-max">
        <Footer />
      </div>
    </main>
  );
}
